import React, { useState } from 'react';
import * as Icons from 'lucide-react';
import { ChevronLeft, ChevronRight, Star, ExternalLink } from 'lucide-react';
import { useSite } from '../../context/SiteContext';
import { useFilter } from '../../context/FilterContext';
import useReveal from '../../hooks/useReveal';
import { isVideoSrc, resolveMediaUrl } from '../admin/MediaUpload';
import { formatMoneyCompact } from '../../lib/format';
import { ShoppingCart } from 'lucide-react';

export default function Originals({ onBuy }) {
  const { data, walletUserEnabled } = useSite();
  const { matches } = useFilter();
  const [tab, setTab] = useState(0);
  const tabs = data.originalsTabs || [];
  const games = (data.originalsGames || []).filter(matches);
  const buyEnabled = walletUserEnabled !== false;

  return (
    <div id="platforms-showcase" className="rounded-xl bg-zinc-950/40 border border-zinc-900 p-3 md:p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-4 overflow-x-auto no-scrollbar">
          {tabs.map((t, idx) => {
            // Backward-compat: tabs can be either a plain string OR { label, link }
            const label = typeof t === 'string' ? t : (t?.label || '');
            const link = typeof t === 'object' ? (t?.link || '') : '';
            const tabSlug = label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
            const isExternal = /^https?:/i.test(link);
            const baseCls = `relative pb-2 text-sm font-bold whitespace-nowrap transition-colors scroll-mt-32 ${tab === idx ? 'text-[color:var(--gold)]' : 'text-zinc-400 hover:text-zinc-200'}`;
            const inner = (
              <>
                {label}
                {tab === idx && <span className="absolute left-0 right-0 -bottom-0.5 h-0.5 bg-[color:var(--gold)] rounded-full" />}
              </>
            );
            // External tab: open in new tab on click. Internal anchor: scroll to it. Plain tab: just filter.
            if (link && isExternal) {
              return (
                <a key={label + idx} id={`tab-${tabSlug}`} data-anchor={`tab-${tabSlug}`} href={link} target="_blank" rel="noreferrer" onClick={() => setTab(idx)} className={baseCls}>
                  {inner}
                </a>
              );
            }
            return (
              <button type="button" key={label + idx} id={`tab-${tabSlug}`} data-anchor={`tab-${tabSlug}`} onClick={() => { setTab(idx); if (link) window.location.hash = link.replace(/^#/, ''); }} className={baseCls}>
                {inner}
              </button>
            );
          })}
        </div>
        <div className="hidden md:flex items-center gap-1.5">
          <button type="button" className="w-7 h-7 rounded-full bg-zinc-900 hover:bg-zinc-800 text-zinc-300 flex items-center justify-center"><ChevronLeft size={16} /></button>
          <button type="button" className="w-7 h-7 rounded-full bg-zinc-900 hover:bg-zinc-800 text-zinc-300 flex items-center justify-center"><ChevronRight size={16} /></button>
        </div>
      </div>

      <div className="grid grid-cols-3 md:grid-cols-4 gap-2.5">
        {games.length === 0 ? (
          <div className="col-span-full text-zinc-500 text-sm text-center py-8" data-testid="platforms-empty">No platforms match your filter.</div>
        ) : games.map((g, i) => <PlatformCard key={g.id} g={g} delay={i * 70} onBuy={() => onBuy?.('platform', g)} buyEnabled={buyEnabled} />)}
      </div>
    </div>
  );
}

function PlatformCard({ g, delay, onBuy, buyEnabled = true }) {
  const Icon = Icons[g.icon] || Icons.Server;
  const { ref, visible } = useReveal();
  const hasVideo = isVideoSrc(g.video);
  const onClick = () => {
    if (g.link) {
      if (/^https?:/.test(g.link)) window.open(g.link, '_blank');
      else window.location.hash = g.link;
    }
  };
  return (
    <div ref={ref} id={`platform-${g.id}`} data-anchor={`platform-${g.id}`} onClick={onClick} role={g.link ? 'button' : undefined} tabIndex={g.link ? 0 : -1}
      onKeyDown={(e) => { if (g.link && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onClick(); } }}
      style={{ background: g.gradient, aspectRatio: '3 / 4', transitionDelay: `${delay}ms`, cursor: g.link ? 'pointer' : 'default' }}
      className={`group relative rounded-lg overflow-hidden text-left click-ripple transition-all duration-500 ease-out scroll-mt-32
        ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
        hover:scale-[1.04] hover:shadow-[0_12px_32px_-12px_rgba(0,0,0,0.6)]`}
      data-testid={`platform-card-${g.id}`}>
      {hasVideo ? (
        <video key={resolveMediaUrl(g.video)} src={resolveMediaUrl(g.video)} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
      ) : g.image ? (
        <img key={resolveMediaUrl(g.image)} src={resolveMediaUrl(g.image)} alt={g.name} className="absolute inset-0 w-full h-full object-cover" />
      ) : null}
      {(hasVideo || g.image) && <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />}
      {!(hasVideo || g.image) && <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.18),transparent_70%)]" />}
      <div className="absolute inset-0 flex flex-col items-center justify-center p-2 text-center">
        <div className="text-white font-impact text-base md:text-2xl leading-tight drop-shadow">{g.name?.toUpperCase()}</div>
        <div className="text-white/70 text-[9px] md:text-[10px] tracking-wider mt-0.5">{g.category}</div>
        {g.rating && (<div className="mt-1 flex items-center gap-1 text-white/90"><Star size={10} className="fill-[color:var(--gold)] text-[color:var(--gold)]" /><span className="text-[10px] font-bold">{g.rating}</span></div>)}
      </div>
      {g.link && (
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
          <span className="w-12 h-12 rounded-full bg-[color:var(--gold)] flex items-center justify-center text-black"><ExternalLink size={20} /></span>
        </div>
      )}
      {Number(g.price || 0) > 0 && (
        <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded bg-black/70 backdrop-blur-sm text-[color:var(--gold)] text-[10px] font-extrabold notranslate" translate="no" data-testid={`platform-price-${g.id}`}>
          {formatMoneyCompact(g.price)}
        </div>
      )}
      {Number(g.price || 0) > 0 && buyEnabled && (
        <button type="button"
          onClick={(e) => { e.stopPropagation(); onBuy?.(); }}
          className="absolute bottom-1.5 inset-x-1.5 h-7 rounded bg-[color:var(--gold)] hover:brightness-110 text-black text-[11px] font-extrabold flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10"
          data-testid={`platform-buy-${g.id}`}
        >
          <ShoppingCart size={11} /> Buy
        </button>
      )}
    </div>
  );
}
